import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HeartPulse, Play, Power, PowerOff, RotateCcw, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import type { CircuitView, Status } from "../../api/types";
import { useGatewayPermissions } from "../../auth/permissions";
import { DataState, formatTime, PageHeader } from "../../components/ui";

type StatusAction = { item: CircuitView; status: Status };

// CircuitsPage filters channel health and exposes only actions valid for the current state.
// 熔断页按模型和状态筛选，并只展示当前可执行的恢复动作。
export function CircuitsPage() {
  const { can } = useGatewayPermissions();
  const canWrite = can("circuits.write");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const queryString = params.toString();
  const circuits = useQuery({ queryKey: ["circuits", queryString], queryFn: () => api.circuits(queryString), refetchInterval: 5_000 });
  const models = useQuery({ queryKey: ["models"], queryFn: api.models });
  const [forceItem, setForceItem] = useState<CircuitView | null>(null);
  const [statusAction, setStatusAction] = useState<StatusAction | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");

  const selectedModel = useMemo(
    () => models.data?.data.find((item) => item.id === Number(params.get("model_id"))),
    [models.data?.data, params],
  );
  const channels = selectedModel?.channels || models.data?.data.flatMap((item) => item.channels) || [];

  const probe = useMutation({
    mutationFn: (id: number) => api.probeNow(id),
    onSuccess: async () => { setMessage("探测请求已受理，渠道将在探测成功后恢复流量"); await refreshCircuits(); },
  });
  const forceClose = useMutation({
    mutationFn: () => api.forceClose(forceItem!.channel_id, forceItem!.version, reason),
    onSuccess: async () => { setMessage("渠道已强制恢复，后续失败仍会重新熔断"); closeDialog(); await refreshCircuits(); },
  });
  const updateStatus = useMutation({
    mutationFn: () => api.updateChannelStatus(statusAction!.item.channel_id, statusAction!.status, statusAction!.item.channel_updated_at, reason),
    onSuccess: async (result) => {
      setMessage(statusAction?.status === "enabled"
        ? result.refresh_pending ? "恢复请求已保存，渠道闸门仍在安全收敛" : "渠道已恢复使用，原熔断状态保持不变"
        : result.refresh_pending ? "渠道已全局暂停，配置刷新仍在收敛" : "渠道已全局暂停，所有业务方和 Key 均不再向它提交新任务");
      closeDialog();
      await Promise.all([refreshCircuits(), queryClient.invalidateQueries({ queryKey: ["models"] })]);
    },
  });

  function refreshCircuits() {
    return queryClient.invalidateQueries({ queryKey: ["circuits"] });
  }
  function closeDialog() {
    setForceItem(null);
    setStatusAction(null);
    setReason("");
  }
  function updateFilter(key: string, value: string) {
    setMessage("");
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === "model_id") next.delete("channel_id");
    setParams(next);
  }
  function clearFilters() {
    setMessage("");
    setParams({});
  }

  const actionError = probe.error || forceClose.error || updateStatus.error;
  return (
    <>
      <PageHeader title="渠道运行状态" description="处理运行中的全局暂停、熔断和恢复，不在这里修改上游接入配置。" />
      <div className="operation-scope-note"><div><strong>配置和运行分开管理</strong><span>渠道、Key、模型映射、价格和发布请到“上游接入”；这里仅处理线上流量状态。</span></div><Link className="secondary-button compact" to="/manage/providers">前往上游接入</Link></div>
      <div className="circuit-filter-panel">
        <CircuitSelect label="模型" value={params.get("model_id") || ""} onChange={(value) => updateFilter("model_id", value)} options={(models.data?.data || []).map((item) => ({ value: String(item.id), label: item.display_name }))} />
        <CircuitSelect label="渠道" value={params.get("channel_id") || ""} onChange={(value) => updateFilter("channel_id", value)} options={channels.map((item) => ({ value: String(item.id), label: item.display_name }))} />
        <CircuitSelect label="渠道开关" value={params.get("channel_status") || ""} onChange={(value) => updateFilter("channel_status", value)} options={[{ value: "enabled", label: "使用中" }, { value: "disabled", label: "已暂停" }]} />
        <CircuitSelect label="熔断状态" value={params.get("circuit_state") || ""} onChange={(value) => updateFilter("circuit_state", value)} options={[{ value: "closed", label: "健康" }, { value: "open", label: "熔断中" }, { value: "half_open", label: "探测中" }]} />
        <CircuitSelect label="渠道类型" value={params.get("channel_class") || ""} onChange={(value) => updateFilter("channel_class", value)} options={[{ value: "official", label: "官方" }, { value: "partner", label: "合作方" }, { value: "relay", label: "中转" }]} />
        <button type="button" className="icon-button" disabled={!queryString} onClick={clearFilters} title="清除筛选" aria-label="清除筛选"><X size={17} /></button>
        <span className="circuit-filter-count">{circuits.data?.data?.length || 0} 条</span>
      </div>
      {message ? <div className="inline-success top-message">{message}</div> : null}
      {actionError ? <div className="inline-error top-message">{actionError.message}</div> : null}
      <DataState loading={circuits.isLoading || models.isLoading} error={circuits.error || models.error} empty={!circuits.data?.data?.length} emptyTitle="没有匹配的渠道状态" emptyDescription="调整模型、渠道或状态筛选">
        <div className="table-wrap">
          <table>
            <thead><tr><th>渠道</th><th>模型</th><th>调度状态</th><th>最近错误</th><th>恢复进度</th><th>下次探测</th><th>操作</th></tr></thead>
            <tbody>{circuits.data?.data?.map((item) => <tr key={item.channel_id} className={item.state !== "closed" || item.channel_status === "disabled" ? "attention-table-row" : ""}>
              <td><strong>{item.channel_name}</strong><small className="cell-note">{item.channel_code} · {channelClassLabel(item.channel_class)}</small></td>
              <td>{item.model_key}</td>
              <td><ChannelDispatchState item={item} /></td>
              <td>{item.last_error_kind || "-"}<small className="cell-note">{item.last_transition_reason || "无异常记录"}</small></td>
              <td className="recovery-progress">{item.recovery_attempt} 次恢复<small className="cell-note">累计 {item.failure_count} 次失败</small></td>
              <td>{formatTime(item.next_probe_at)}</td>
              <td>{canWrite ? <CircuitActions item={item} pending={probe.isPending || updateStatus.isPending} onProbe={() => probe.mutate(item.channel_id)} onForce={() => { setForceItem(item); setReason(""); }} onStatus={(status) => { setStatusAction({ item, status }); setReason(""); }} /> : <span className="operation-note">只读</span>}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </DataState>

      {canWrite && forceItem ? <ActionDialog title={`强制恢复 ${forceItem.channel_name}`} description="该操作会立即允许后续请求进入渠道。" warning="不会删除历史失败，渠道再次失败后仍按阈值重新熔断。" reason={reason} pending={forceClose.isPending} confirmLabel="确认强制恢复" danger onReason={setReason} onClose={closeDialog} onConfirm={() => forceClose.mutate()} /> : null}
      {canWrite && statusAction ? <ActionDialog title={`${statusAction.status === "enabled" ? "恢复使用" : "暂停"} ${statusAction.item.channel_name}`} description={statusAction.status === "enabled" ? "恢复后所有业务方和 Key 可重新按各自策略使用该渠道。" : "暂停是全局操作，所有业务方和 Key 都不能再向该渠道提交新任务。"} warning={statusAction.status === "enabled" && statusAction.item.state !== "closed" ? "该渠道仍处于熔断状态，恢复使用后还需要立即探测或强制恢复。" : "已经提交到上游的任务会继续执行，不会被取消或重复提交。"} reason={reason} pending={updateStatus.isPending} confirmLabel={statusAction.status === "enabled" ? "确认恢复使用" : "确认全局暂停"} danger={statusAction.status === "disabled"} onReason={setReason} onClose={closeDialog} onConfirm={() => updateStatus.mutate()} /> : null}
    </>
  );
}

function CircuitActions({ item, pending, onProbe, onForce, onStatus }: { item: CircuitView; pending: boolean; onProbe: () => void; onForce: () => void; onStatus: (status: Status) => void }) {
  if (item.channel_status === "disabled") {
    return <button className="secondary-button compact" disabled={pending} onClick={() => onStatus("enabled")}><Power size={14} />恢复使用</button>;
  }
  return <div className="row-actions">
    {item.state === "open" ? <button className="secondary-button compact" disabled={pending} onClick={onProbe}><Play size={14} />立即探测</button> : null}
    {item.state === "half_open" ? <span className="operation-note"><HeartPulse size={14} />探测中</span> : null}
    {item.state !== "closed" ? <button className="icon-button" title="强制恢复" disabled={pending} onClick={onForce}><RotateCcw size={16} /></button> : <span className="operation-note">无需恢复</span>}
    <button className="secondary-button compact danger-text" title="暂停渠道" disabled={pending} onClick={() => onStatus("disabled")}><PowerOff size={15} />暂停渠道</button>
  </div>;
}

function ChannelDispatchState({ item }: { item: CircuitView }) {
  if (item.channel_status === "disabled") return <div className="dispatch-state blocked"><strong>不可调度</strong><small>渠道已全局暂停，熔断状态为 {circuitStateLabel(item.state)}</small></div>;
  if (item.state === "open") return <div className="dispatch-state blocked"><strong>不可调度</strong><small>渠道熔断中</small></div>;
  if (item.state === "half_open") return <div className="dispatch-state pending"><strong>恢复探测中</strong><small>暂未恢复正常流量</small></div>;
  return <div className="dispatch-state ready"><strong>可调度</strong><small>渠道开启，熔断器健康</small></div>;
}

function circuitStateLabel(value: string) {
  return { closed: "健康", open: "熔断中", half_open: "探测中" }[value] || value;
}

function CircuitSelect({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (value: string) => void }) {
  return <label className="compact-field"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}><option value="">全部</option>{options.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>;
}

function ActionDialog({ title, description, warning, reason, pending, confirmLabel, danger, onReason, onClose, onConfirm }: { title: string; description: string; warning: string; reason: string; pending: boolean; confirmLabel: string; danger?: boolean; onReason: (reason: string) => void; onClose: () => void; onConfirm: () => void }) {
  return <div className="dialog-backdrop"><div className="dialog" role="dialog" aria-modal="true">
    <div className="dialog-header"><div><h2>{title}</h2><p>{description}</p></div><button className="icon-button" onClick={onClose} aria-label="关闭"><X size={18} /></button></div>
    <div className="dialog-warning"><HeartPulse size={17} />{warning}</div>
    <label className="field"><span>操作原因</span><textarea rows={3} value={reason} onChange={(event) => onReason(event.target.value)} placeholder="填写已完成的检查或本次操作原因" /></label>
    <div className="dialog-actions"><button className="secondary-button" onClick={onClose}>取消</button><button className={danger ? "danger-button" : "primary-button"} disabled={!reason.trim() || pending} onClick={onConfirm}>{pending ? "处理中" : confirmLabel}</button></div>
  </div></div>;
}

function channelClassLabel(value: string) {
  return { official: "官方", partner: "合作方", relay: "中转" }[value] || value;
}
